import { useCallback, useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useHistory, useParams } from 'react-router-dom'

import { useAuth } from 'contexts/AuthContext'
import { useDashboardModals } from 'contexts/DashboardModalsContext'
import type { DashboardStatus, DashboardType } from 'generated/graphql'
import { JobsFieldSort } from 'generated/graphql'
import { useSearchParams } from 'hooks'
import { useDashboardsQueries } from 'hooks-queries/dashboards'
import { UserStore } from 'stores'
import { analyticsTracker } from 'utils'

import { GET_JOB_ERROR_LOG, PAGINATION_LIMIT, SORT_ORDER } from './dashboard-list.consts'
import type { IDashboardList, IDashboardListProps } from './dashboard-list.types'

export const useDashboardListModel = ({ isFolder }: IDashboardListProps): IDashboardList => {
  const searchParams = useSearchParams()
  const termParam = searchParams.get('term')
  const sort = searchParams.get('sortBy')
  const typeSelect = searchParams.get('typeSelect')
  const createdByZai = searchParams.get('createdByZai')
  const createdByDraft = searchParams.get('draft')
  const createdByDone = searchParams.get('done')
  const [search, setSearch] = useState(termParam)

  const history = useHistory()
  const { folderId } = useParams<{ folderId?: string }>()
  const { setIsCreateDashboardModalOpen } = useDashboardModals()
  const { plan } = useAuth()
  const maxJobs = plan?.jobs
  const isUserViewer = UserStore?.getRole === 'viewer'

  const getQueryVariables = useCallback(
    (cursor = null) => {
      const shouldUseFolderId = folderId && isFolder
      return {
        pagination: { cursor, take: PAGINATION_LIMIT },
        term: termParam,
        ...(!!createdByZai && { automaticallyGenerated: !!createdByZai }),
        status: (createdByDraft && !createdByDone
          ? createdByDraft
          : !createdByDraft && createdByDone
            ? createdByDone
            : null) as DashboardStatus,
        type: (typeSelect || null) as DashboardType,
        sort: {
          order: SORT_ORDER[sort] || SORT_ORDER.updated_at,
          field: (sort as JobsFieldSort) || JobsFieldSort.UpdatedAt,
        },
        withoutFolder: !isFolder,
        ...(shouldUseFolderId && { folderId }),
      }
    },
    [createdByZai, termParam, createdByDraft, createdByDone, typeSelect, sort, isFolder, folderId]
  )

  const queryVariables = useMemo(() => getQueryVariables(), [getQueryVariables])

  const { data, error, fetchMore, refetch, networkStatus, loading } = useDashboardsQueries({
    queryName: 'getListWorkspaceJobPaginated',
    queryVariables,
    errorLog: GET_JOB_ERROR_LOG,
    options: {
      fetchPolicy: 'network-only',
    },
  })

  const handleRefetch = () => {
    refetch()
  }

  const handleClickOnCreateNewDashboard = () => {
    const { START_NEW_JOB } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(START_NEW_JOB)

    setIsCreateDashboardModalOpen(true)
  }

  const jobs = useMemo(() => {
    return data?.listWorkspaceJobPaginated.data || []
  }, [data])
  const jobsRows = data?.listWorkspaceJobPaginated?.totalCount || 0
  const jobsValidationLimit = jobsRows < maxJobs

  const isLoading = [NetworkStatus.loading, NetworkStatus.refetch].includes(networkStatus)
  const cursor = data?.listWorkspaceJobPaginated?.cursor
  const hasMore = Boolean(cursor)
  const isEmpty = !jobs?.length

  const loadMore = async () => {
    if (loading || !hasMore || NetworkStatus.fetchMore === networkStatus) return

    try {
      await fetchMore({
        variables: getQueryVariables(cursor),
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.listWorkspaceJobPaginated?.data?.length) {
            return prev
          }

          const mergedResult = {
            listWorkspaceJobPaginated: {
              ...fetchMoreResult.listWorkspaceJobPaginated,
              data: [...prev.listWorkspaceJobPaginated.data, ...fetchMoreResult.listWorkspaceJobPaginated.data],
            },
          }

          return mergedResult
        },
      })
    } catch {
      // Errors are reported centrally by the Apollo ErrorLink.
    }
  }

  const shouldDisplayEmptyState = isEmpty && !loading && isFolder

  const [sentryRef] = useInfiniteScroll({
    loading: loading || NetworkStatus.fetchMore === networkStatus,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  function handleChipClick(paramKey: 'draft' | 'done' | 'createdByZai') {
    if (paramKey === 'done') {
      searchParams.delete('draft')
    }
    if (paramKey === 'draft') {
      searchParams.delete('done')
    }
    if (searchParams.get(paramKey)) {
      searchParams.delete(paramKey)
    } else {
      searchParams.set(paramKey, paramKey)
    }

    const { DASHBOARD_SEARCH } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(DASHBOARD_SEARCH.FILTER_STATUS, {
      'Filter status': paramKey,
    })

    history.push({ search: searchParams.toString() })
  }

  function handleFilterChange(key: string, value: string) {
    if (!value) {
      searchParams.delete(key)
    } else {
      searchParams.set(key, value)
    }

    const { DASHBOARD_SEARCH } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(DASHBOARD_SEARCH.FILTER_TYPE, {
      'Filter type': value,
    })

    history.push({ search: searchParams.toString() })
  }

  function trackSortBy(value) {
    const { DASHBOARD_SEARCH } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(DASHBOARD_SEARCH.SORTY_BY, {
      'Sort by': value,
    })
  }

  const handleChangeSearch = (value: string) => {
    setSearch(value)
  }

  function handleSearch(term: string) {
    if (term) {
      searchParams.set('term', term)
    } else {
      searchParams.delete('term')
    }

    const { DASHBOARD_SEARCH } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(DASHBOARD_SEARCH.SEARCH, {
      Term: term,
    })

    history.push({ search: searchParams.toString() })
  }

  return {
    jobs,
    jobsRows,
    jobsValidationLimit,
    error: Boolean(error),
    refetch: handleRefetch,
    handleClickOnCreateNewDashboard,
    loading: isLoading,
    hasMore,
    sentryRef,
    handleChipClick,
    handleFilterChange,
    trackSortBy,
    handleChangeSearch,
    search,
    maxJobs,
    createdByDone,
    createdByDraft,
    createdByZai,
    typeSelect,
    isUserViewer,
    handleSearch,
    isFolder,
    isEmpty,
    shouldDisplayEmptyState,
  }
}
